/**
 * The store's numbers the Estreia shows (NEWS-23…26): the Início's and the
 * Analytics' own queries for the last 30 days (start of the day 30 days ago
 * → now) and the 30 before, with their `queryKey`s, so the edition reads the
 * cache those pages already filled. Each fact is loading, ready or missing
 * (failed, 402 or turned off); the view-models pick the alternative texts.
 */
import {
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import { DateTime } from 'luxon';
import type {
  CampaignDto,
  CampaignPerformanceResponseDto,
  TenantAppSessionsChartResponseDto,
  TenantCampaignOrdersChartResponseDto,
  TenantHomeResponseDto,
  TenantSalesMetricsResponseDto,
  TenantUserMetricsResponseDto,
  WebsiteBannerDto,
} from '@/generated/korza-api-client/types.gen';
import {
  appSessionsChartQuery,
  campaignOrdersChartQuery,
  campaignPerformanceQuery,
  salesMetricsQuery,
  userMetricsQuery,
} from '@/hooks/analytics/analytics-queries';
import { useCampaigns } from '@/hooks/use-campaigns';
import { useGetCouponBanner } from '@/hooks/use-coupon-banner';
import { useFeatureFlags } from '@/hooks/use-feature-flags';
import { useGetWebsiteBanner } from '@/hooks/use-get-website-banner';
import { useNextScheduledCampaign } from '@/hooks/use-next-scheduled-campaign';
import {
  currencyForDisplay,
  useStoreCurrency,
} from '@/hooks/use-store-currency';
import { useTenant } from '@/hooks/use-tenant';
import { tenantHomeQueryOptions } from '@/hooks/use-tenant-home';
import { useGetWelcomeNotification } from '@/hooks/use-welcome-notification';
import { useWithPrevious, type WithPrevious } from '@/hooks/use-with-previous';
import type { CouponBannerDto, WelcomeNotificationDto } from '@/lib/dtos';
import { previousRange, type DateRange } from '@/lib/period-comparison';

/** A number of the store: still loading, there, or not available (NEWS-25/26). */
export type Fact<T> =
  | { state: 'loading' }
  | { state: 'ready'; value: T }
  | { state: 'missing' };

/** The last 30 days and the 30 before; `previous` is `null` when its call failed. */
export interface Compared<T> {
  current: T;
  previous: T | null;
}

export interface EstreiaPeriod {
  /** Start of the day 30 days ago → now (the Início's "30 dias"). */
  current: DateRange;
  /** The 30 days before, ending right before `current` starts. */
  previous: DateRange | null;
}

export interface EstreiaStore {
  name: string;
  /** `null` → the `Store` icon. */
  iconUrl: string | null;
  /** `null` → "Convida quem está no seu site". */
  domain: string | null;
}

export interface EstreiaFacts {
  store: EstreiaStore;
  period: EstreiaPeriod;
  /** `undefined` while loading (skeleton); BRL when it failed, as the Início. */
  currency: string | undefined;
  /** `/analytics/home` by day (Início). */
  home: Fact<Compared<TenantHomeResponseDto>>;
  /** `/analytics/home` by hour, today 00:00 → now ("Hoje até agora"). */
  todaySoFar: Fact<TenantHomeResponseDto>;
  /** Nearest scheduled campaign; `null` without one. */
  nextCampaign: Fact<CampaignDto | null>;
  /** Campaigns sent in the period (the Início's chart markers). */
  periodCampaigns: Fact<CampaignDto[]>;
  /** The most recently sent campaign (any date); `null` without one. */
  latestSentCampaign: Fact<CampaignDto | null>;
  sales: Fact<Compared<TenantSalesMetricsResponseDto>>;
  users: Fact<Compared<TenantUserMetricsResponseDto>>;
  campaignPerformance: Fact<Compared<CampaignPerformanceResponseDto>>;
  /** Sessions by day. */
  sessions: Fact<Compared<TenantAppSessionsChartResponseDto>>;
  /** Campaign-attributed orders and revenue by day. */
  campaignOrdersChart: Fact<TenantCampaignOrdersChartResponseDto>;
  /** `null` = not set up. */
  couponBanner: Fact<CouponBannerDto | null>;
  /** `null` = not set up. */
  welcomeNotification: Fact<WelcomeNotificationDto | null>;
  /** `null` = not set up. */
  websiteBanner: Fact<WebsiteBannerDto | null>;
}

/** Same limits as the Início's chart markers and "Últimas campanhas". */
const MARKER_CAMPAIGNS_LIMIT = 100;
const RECENT_CAMPAIGNS_LIMIT = 3;

const LOADING = { state: 'loading' } as const;
const MISSING = { state: 'missing' } as const;

/** Start of the Início's 30-day window for `now`. */
function windowStart(now: DateTime): string {
  return now.minus({ days: 30 }).startOf('day').toISO() ?? '';
}

/** The Início's "30 dias" ending at `now`, and the 30 days before it. */
export function estreiaPeriod(now: DateTime): EstreiaPeriod {
  const current = { from: windowStart(now), to: now.toISO() ?? '' };
  return {
    current,
    previous: previousRange({ ...current, preset: 'last_30_days' }),
  };
}

/**
 * Where the edition's 30 days end: the end of the Início's own 30-day window
 * when it is cached (the Início fixes `now` when it mounts), so both read
 * the same queries; otherwise now.
 */
function periodEnd(queryClient: QueryClient, tenantSlug: string): string {
  const now = DateTime.now();
  const ends = queryClient
    .getQueryCache()
    .findAll({ queryKey: ['metrics-home', tenantSlug, windowStart(now)] })
    .map((query) => query.queryKey[3])
    .filter((end): end is string => typeof end === 'string')
    .sort();
  return ends.at(-1) ?? now.toISO() ?? '';
}

/**
 * The period's end, fixed the first time a slide asks for it: every slide of
 * the opening (and a reopening within the cache time) uses the same period.
 */
function useEstreiaNow(tenantSlug: string): DateTime {
  const queryClient = useQueryClient();
  const end = useQuery({
    queryKey: ['whats-new', 'estreia-period-end', tenantSlug],
    queryFn: () => periodEnd(queryClient, tenantSlug),
    initialData: () => periodEnd(queryClient, tenantSlug),
    staleTime: Infinity,
  });
  return DateTime.fromISO(end.data);
}

function factOf<T>(
  query: Pick<UseQueryResult<T, Error>, 'data' | 'isError'>
): Fact<T> {
  if (query.data !== undefined) return { state: 'ready', value: query.data };
  return query.isError ? MISSING : LOADING;
}

function comparedOf<T>({
  current,
  previous,
}: WithPrevious<T>): Fact<Compared<T>> {
  const fact = factOf(current);
  if (fact.state !== 'ready') return fact;
  return {
    state: 'ready',
    value: {
      current: fact.value,
      previous: previous.isError ? null : (previous.data ?? null),
    },
  };
}

export function useEstreiaFacts(): EstreiaFacts {
  const tenant = useTenant();
  const slug = tenant.slug;
  const now = useEstreiaNow(slug);
  const period = estreiaPeriod(now);
  const range = { ...period.current, preset: 'last_30_days' as const };

  const flags = useFeatureFlags();
  const analyticsEnabled = flags.data?.analyticsEnabled === true;
  /** Analytics numbers only for stores with Analytics on (the page shows samples otherwise). */
  const analytics = <T>(fact: Fact<T>): Fact<T> => {
    if (flags.data === undefined) return flags.isError ? MISSING : LOADING;
    return analyticsEnabled ? fact : MISSING;
  };

  const home = useWithPrevious(
    (r) =>
      tenantHomeQueryOptions(slug, {
        fromDate: r.from,
        toDate: r.to,
        granularity: 'day',
      }),
    range
  );
  const todaySoFar = useQuery(
    tenantHomeQueryOptions(slug, {
      fromDate: now.startOf('day').toISO() ?? '',
      toDate: now.toISO() ?? '',
      granularity: 'hour',
    })
  );
  const currency = useStoreCurrency();

  const periodCampaigns = useCampaigns(
    {
      hasBeenSent: 'true',
      launchDateMin: period.current.from,
      launchDateMax: period.current.to,
    },
    MARKER_CAMPAIGNS_LIMIT,
    0
  );
  const recentCampaigns = useCampaigns(
    { hasBeenSent: 'true' },
    RECENT_CAMPAIGNS_LIMIT,
    0
  );
  const next = useNextScheduledCampaign();

  const sales = useWithPrevious(
    (r) => ({ ...salesMetricsQuery(slug, r), enabled: analyticsEnabled }),
    range
  );
  const users = useWithPrevious(
    (r) => ({ ...userMetricsQuery(slug, r), enabled: analyticsEnabled }),
    range
  );
  const campaignPerformance = useWithPrevious(
    (r) => ({
      ...campaignPerformanceQuery(slug, r),
      enabled: analyticsEnabled,
    }),
    range
  );
  const sessions = useWithPrevious(
    (r) => ({
      ...appSessionsChartQuery(slug, r, 'day'),
      enabled: analyticsEnabled,
    }),
    range
  );
  const campaignOrdersChart = useQuery({
    ...campaignOrdersChartQuery(slug, period.current, 'day'),
    enabled: analyticsEnabled,
  });

  const couponBanner = useGetCouponBanner();
  const welcomeNotification = useGetWelcomeNotification();
  const websiteBanner = useGetWebsiteBanner();

  const inPeriod = factOf(periodCampaigns);
  const recent = factOf(recentCampaigns);

  return {
    store: {
      name: tenant.name,
      iconUrl: tenant.iconUrl || null,
      domain: tenant.storeWebsiteDomain || null,
    },
    period,
    currency: currencyForDisplay(currency),
    home: comparedOf(home),
    todaySoFar: factOf(todaySoFar),
    nextCampaign: next.query.isError
      ? MISSING
      : next.campaign === undefined
        ? LOADING
        : { state: 'ready', value: next.campaign },
    periodCampaigns:
      inPeriod.state === 'ready'
        ? { state: 'ready', value: inPeriod.value.data }
        : inPeriod,
    latestSentCampaign:
      recent.state === 'ready'
        ? { state: 'ready', value: recent.value.data[0] ?? null }
        : recent,
    sales: analytics(comparedOf(sales)),
    users: analytics(comparedOf(users)),
    campaignPerformance: analytics(comparedOf(campaignPerformance)),
    sessions: analytics(comparedOf(sessions)),
    campaignOrdersChart: analytics(factOf(campaignOrdersChart)),
    couponBanner: factOf(couponBanner),
    welcomeNotification: factOf(welcomeNotification),
    // Without a banner id there is nothing to fetch; with one, the hook
    // answers `null` when the call fails.
    websiteBanner: !tenant.websiteBannerId
      ? { state: 'ready', value: null }
      : websiteBanner.data === null || websiteBanner.isError
        ? MISSING
        : factOf(websiteBanner),
  };
}
